import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowUp, ChatCircle, MapPin, ArrowSquareOut, Trash, BookmarkSimple,
    AirplaneTilt, Buildings, Camera, PencilSimple,
} from "@phosphor-icons/react";
import AuthImage from "../components/AuthImage";
import CreatePostDialog from "../components/CreatePostDialog";

const CAT_META = {
    flight: { Icon: AirplaneTilt, label: "Flight" },
    hotel: { Icon: Buildings, label: "Hotel" },
    destination: { Icon: MapPin, label: "Destination" },
    activity: { Icon: Camera, label: "Activity" },
};

function relTime(d) {
    const diff = (Date.now() - new Date(d).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(d).toLocaleDateString();
}

export default function PostDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [posting, setPosting] = useState(false);
    const [voting, setVoting] = useState(false);
    const [bmBusy, setBmBusy] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const [p, c] = await Promise.all([
                    api.get(`/posts/${id}`),
                    api.get(`/posts/${id}/comments`),
                ]);
                if (!cancel) {
                    setPost(p.data);
                    setComments(c.data);
                }
            } catch (e) {
                if (!cancel) {
                    toast.error("Post not found");
                    navigate("/feed");
                }
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [id, navigate]);

    const upvote = async () => {
        if (voting) return;
        setVoting(true);
        try {
            const { data } = await api.post(`/posts/${id}/upvote`);
            setPost((p) => ({ ...p, upvotes: data.upvotes, has_upvoted: data.upvoted }));
        } finally { setVoting(false); }
    };

    const bookmark = async () => {
        if (bmBusy) return;
        setBmBusy(true);
        try {
            const { data } = await api.post(`/posts/${id}/bookmark`);
            setPost((p) => ({ ...p, is_bookmarked: data.bookmarked }));
            toast.success(data.bookmarked ? "Saved to your trip plan" : "Removed from saved");
        } catch (_) {
            toast.error("Could not update");
        } finally { setBmBusy(false); }
    };

    const submitComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;
        setPosting(true);
        try {
            const { data } = await api.post(`/posts/${id}/comments`, { content: newComment.trim() });
            setComments((cs) => [...cs, data]);
            setPost((p) => ({ ...p, comments_count: p.comments_count + 1 }));
            setNewComment("");
            toast.success("Comment added");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to comment");
        } finally { setPosting(false); }
    };

    const deletePost = async () => {
        if (!window.confirm("Delete this post permanently?")) return;
        try {
            await api.delete(`/posts/${id}`);
            toast.success("Post deleted");
            navigate("/feed");
        } catch (_) { toast.error("Could not delete"); }
    };

    if (loading || !post) {
        return (
            <div className="min-h-screen bg-[#FAFAFA]">
                <Navbar />
                <div className="max-w-3xl mx-auto px-4 py-12 text-center text-stone-500 text-sm">Loading…</div>
            </div>
        );
    }

    const meta = CAT_META[post.category] || CAT_META.destination;
    const Icon = meta.Icon;
    const initials = (post.user_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
    const canDelete = user && (user.id === post.user_id || user.role === "admin");
    const canEdit = user && user.id === post.user_id;

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="post-detail-page">
            <Navbar />
            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
                <Link
                    to="/feed"
                    data-testid="back-to-feed-link"
                    className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#990000] mb-6"
                >
                    <ArrowLeft size={16} /> Back to feed
                </Link>

                <article className="bg-white border border-stone-200 rounded-xl p-6 sm:p-8">
                    <div className="flex items-center gap-2 flex-wrap mb-4">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#FFF4CC] border border-[#FFE680] text-[#5C4400] text-[11px] font-bold uppercase tracking-wider">
                            <Icon size={12} weight="fill" />
                            {meta.label}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-stone-500">
                            <MapPin size={12} /> {post.city}
                        </span>
                        <span className="text-xs text-stone-400">·</span>
                        <span className="text-xs text-stone-500">{relTime(post.created_at)}</span>
                        {canEdit && (
                            <button
                                data-testid="edit-post-btn"
                                onClick={() => setEditOpen(true)}
                                className="ml-auto inline-flex items-center gap-1 text-xs text-stone-400 hover:text-[#990000]"
                            >
                                <PencilSimple size={14} /> Edit
                            </button>
                        )}
                        {canDelete && (
                            <button
                                data-testid="delete-post-btn"
                                onClick={deletePost}
                                className={`${canEdit ? "" : "ml-auto"} inline-flex items-center gap-1 text-xs text-stone-400 hover:text-[#990000]`}
                            >
                                <Trash size={14} /> Delete
                            </button>
                        )}
                    </div>

                    <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A0A0A] tracking-tight leading-tight mb-4">
                        {post.title}
                    </h1>

                    {post.image_url && (
                        <AuthImage
                            src={post.image_url}
                            alt={post.title}
                            className="w-full h-72 sm:h-96 object-cover rounded-xl border border-stone-200 mb-6"
                            testId="post-detail-image"
                        />
                    )}

                    <Link
                        to={`/users/${post.user_id}`}
                        data-testid="post-detail-author-link"
                        className="inline-flex items-center gap-2 mb-6 group"
                    >
                        <div className="w-8 h-8 rounded-full bg-[#990000] text-[#FFCC00] flex items-center justify-center text-xs font-bold">
                            {initials}
                        </div>
                        <div>
                            <div className="text-sm font-medium text-stone-700 group-hover:text-[#990000]">{post.user_name}</div>
                            <div className="text-[11px] text-stone-400">USC Trojan</div>
                        </div>
                    </Link>

                    <div className="prose prose-stone max-w-none mb-6">
                        <p className="text-stone-700 leading-relaxed whitespace-pre-line">{post.description}</p>
                    </div>

                    {post.url && (
                        <a
                            data-testid="post-detail-url-link"
                            href={post.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#990000]/30 text-[#990000] hover:bg-[#990000]/5 text-sm font-medium mb-4"
                        >
                            <ArrowSquareOut size={16} weight="bold" />
                            <span className="truncate max-w-xs">{post.url}</span>
                        </a>
                    )}

                    {post.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                            {post.tags.map((t) => (
                                <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-600 font-medium">#{t}</span>
                            ))}
                        </div>
                    )}

                    <div className="mt-6 pt-6 border-t border-stone-100 flex items-center gap-3 flex-wrap">
                        <button
                            data-testid="post-detail-upvote-btn"
                            onClick={upvote}
                            disabled={voting}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                                post.has_upvoted
                                    ? "bg-[#990000] border-[#990000] text-[#FFCC00]"
                                    : "border-stone-200 text-stone-600 hover:border-[#990000] hover:text-[#990000]"
                            }`}
                        >
                            <ArrowUp size={16} weight={post.has_upvoted ? "fill" : "bold"} />
                            <span>{post.upvotes}</span>
                        </button>
                        <button
                            data-testid="post-detail-bookmark-btn"
                            onClick={bookmark}
                            disabled={bmBusy}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                                post.is_bookmarked
                                    ? "bg-[#FFF4CC] border-[#FFCC00] text-[#5C4400]"
                                    : "border-stone-200 text-stone-600 hover:border-[#990000] hover:text-[#990000]"
                            }`}
                        >
                            <BookmarkSimple size={16} weight={post.is_bookmarked ? "fill" : "regular"} />
                            <span>{post.is_bookmarked ? "Saved" : "Save"}</span>
                        </button>
                        <div className="inline-flex items-center gap-2 text-sm text-stone-500 ml-auto">
                            <ChatCircle size={16} /> {post.comments_count} comments
                        </div>
                    </div>
                </article>

                {/* Comments */}
                <section className="mt-8" data-testid="comments-section">
                    <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-600 mb-4">
                        Discussion ({comments.length})
                    </div>

                    <form onSubmit={submitComment} className="bg-white border border-stone-200 rounded-xl p-4 mb-6">
                        <Textarea
                            data-testid="comment-input"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add to the discussion…"
                            rows={3}
                            className="border-stone-200 focus-visible:ring-[#990000] focus-visible:border-[#990000] mb-3 resize-none"
                            maxLength={1000}
                        />
                        <div className="flex justify-end">
                            <Button
                                type="submit"
                                data-testid="comment-submit-btn"
                                disabled={posting || !newComment.trim()}
                                className="bg-[#990000] hover:bg-[#7A0000] text-white h-9 px-5 text-sm"
                            >
                                {posting ? "Posting…" : "Comment"}
                            </Button>
                        </div>
                    </form>

                    <div className="space-y-3">
                        {comments.length === 0 ? (
                            <div className="text-sm text-stone-400 text-center py-6">Be the first to start the conversation.</div>
                        ) : (
                            comments.map((c) => {
                                const ci = (c.user_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
                                return (
                                    <div key={c.id} data-testid={`comment-${c.id}`} className="bg-white border border-stone-200 rounded-lg p-4 flex gap-3">
                                        <Link
                                            to={`/users/${c.user_id}`}
                                            className="w-8 h-8 rounded-full bg-[#990000] text-[#FFCC00] flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                                        >
                                            {ci}
                                        </Link>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Link to={`/users/${c.user_id}`} className="text-sm font-medium text-stone-700 hover:text-[#990000]">
                                                    {c.user_name}
                                                </Link>
                                                <span className="text-xs text-stone-400">· {relTime(c.created_at)}</span>
                                            </div>
                                            <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-line">{c.content}</p>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </section>
            </main>

            {canEdit && (
                <CreatePostDialog
                    editingPost={post}
                    open={editOpen}
                    onOpenChange={setEditOpen}
                    onUpdated={(updated) => setPost((cur) => ({ ...cur, ...updated }))}
                />
            )}
        </div>
    );
}
